import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/api';
import { resetSocialCallback } from '@/utils/socialAuthCallback';
import type { SocialAuthProvider } from '@/components/ui/SocialAuthButton';
import { clearSocialAuthContext, storeInviteSocialAuthContext } from '@/utils/socialAuthContext';

export function useInviteSocialSignIn(token: string) {
  const { socialLogin } = useAuth();
  const [pendingProvider, setPendingProvider] = useState<SocialAuthProvider>();
  const [error, setError] = useState('');
  const pending = useRef(false);

  useEffect(() => {
    const recover = (event: PageTransitionEvent) => {
      if (!event.persisted || !pending.current) return;
      resetSocialCallback();
      clearSocialAuthContext();
      pending.current = false;
      setPendingProvider(undefined);
      setError('Sign-in was cancelled. Please try again.');
    };
    window.addEventListener('pageshow', recover);
    return () => window.removeEventListener('pageshow', recover);
  }, []);

  const signIn = async (provider: SocialAuthProvider) => {
    if (pending.current) return;
    pending.current = true;
    setPendingProvider(provider);
    setError('');
    try {
      storeInviteSocialAuthContext(token, provider);
      await socialLogin(provider);
    } catch {
      resetSocialCallback();
      clearSocialAuthContext();
      pending.current = false;
      setPendingProvider(undefined);
      setError('Unable to start sign-in. Please try again.');
    }
  };

  return { signIn, pendingProvider, error };
}
